import { useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, CircleDashed, Clock3, PlayCircle } from 'lucide-react';
import { motion, useInView } from 'motion/react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { projectStages, type StageMediaType, type ProjectStage } from '../data/projectStages';

const statusStyles = {
  completed: {
    label: 'Completada',
    icon: CheckCircle2,
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  active: {
    label: 'En ejecución',
    icon: Clock3,
    badge: 'bg-secondary/10 text-secondary border-secondary/20',
  },
  planned: {
    label: 'Próximamente',
    icon: CircleDashed,
    badge: 'bg-slate-100 text-slate-700 border-slate-200',
  },
};

const mediaTypeLabels: Record<'all' | StageMediaType, string> = {
  all: 'Todo',
  photo: 'Fotos',
  video: 'Video',
};

const ITEMS_PER_PAGE = 4;

function mediaTypeBadge(type: StageMediaType) {
  if (type === 'photo') return 'Foto';
  return 'Video';
}

export function Stages() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const [stages, setStages] = useState<ProjectStage[]>(projectStages);
  const [activeStageId, setActiveStageId] = useState(projectStages[0]?.id ?? '');
  const [activeMediaType, setActiveMediaType] = useState<'all' | StageMediaType>('all');
  const [selectedMediaId, setSelectedMediaId] = useState(projectStages[0]?.media[0]?.id ?? '');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/stages.php', { headers: { Accept: 'application/json' } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data?.stages?.length) return;
        setStages(data.stages as ProjectStage[]);
        setActiveStageId((prev) => {
          const stillExists = data.stages.find((s: ProjectStage) => s.id === prev);
          return stillExists ? prev : data.stages[0].id;
        });
      })
      .catch(() => { /* fallback al JSON estático */ });
    return () => { cancelled = true; };
  }, []);

  const activeStage = useMemo(
    () => stages.find((stage) => stage.id === activeStageId) ?? stages[0],
    [activeStageId, stages],
  );

  const filteredMedia = useMemo(() => {
    if (!activeStage) return [];
    if (activeMediaType === 'all') return activeStage.media;
    return activeStage.media.filter((item) => item.type === activeMediaType);
  }, [activeStage, activeMediaType]);

  const pageCount = Math.max(1, Math.ceil(filteredMedia.length / ITEMS_PER_PAGE));
  const paginatedMedia = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredMedia.slice(start, start + ITEMS_PER_PAGE);
  }, [currentPage, filteredMedia]);

  const selectedMedia = useMemo(() => {
    if (!activeStage) return null;
    return (
      paginatedMedia.find((item) => item.id === selectedMediaId) ??
      paginatedMedia[0] ??
      filteredMedia[0] ??
      activeStage.media[0] ??
      null
    );
  }, [activeStage, filteredMedia, paginatedMedia, selectedMediaId]);

  useEffect(() => {
    if (!activeStage) return;
    setActiveMediaType('all');
    setCurrentPage(1);
    setSelectedMediaId(activeStage.media[0]?.id ?? '');
  }, [activeStageId, activeStage]);

  useEffect(() => {
    if (currentPage > pageCount) {
      setCurrentPage(1);
    }
  }, [currentPage, pageCount]);

  useEffect(() => {
    if (!selectedMedia && paginatedMedia.length > 0) {
      setSelectedMediaId(paginatedMedia[0].id);
    }
  }, [paginatedMedia, selectedMedia]);

  if (!activeStage) return null;

  const isUpcomingStage = activeStage.status === 'planned';
  const statusMeta = statusStyles[activeStage.status];
  const StatusIcon = statusMeta.icon;

  return (
    <section id="stages" className="py-20 md:py-28 bg-slate-50 border-t border-slate-200" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            className="inline-flex items-center px-4 py-2 rounded-full border border-primary/10 bg-gradient-to-br from-white to-slate-50 text-slate-700 mb-6 shadow-sm font-medium text-sm"
          >
            Etapas
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl text-slate-950 mb-5 tracking-tight font-bold"
          >
            Cronograma de Entrega
          </motion.h2>
        </div>

        <div className="grid gap-4 md:grid-cols-3 mb-8">
          {stages.map((stage, index) => {
            const isActive = stage.id === activeStage.id;
            const stageStatus = statusStyles[stage.status];
            const StageStatusIcon = stageStatus.icon;
            const stepNumber = (index + 1).toString().padStart(2, '0');
            
            return (
              <motion.button
                key={stage.id}
                type="button"
                initial={{ opacity: 0, y: 16 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.08 + index * 0.08 }}
                onClick={() => setActiveStageId(stage.id)}
                className={`group relative overflow-hidden text-left rounded-2xl border px-6 py-6 transition-all duration-300 ${
                  isActive
                    ? 'bg-primary text-white border-primary shadow-xl shadow-primary/20 scale-[1.02]'
                    : 'bg-white text-slate-900 border-slate-200 hover:border-slate-300 hover:shadow-md'
                }`}
              >
                {/* Indicador de paso decorativo */}
                <div className={`absolute -right-2 -top-2 text-6xl font-black opacity-[0.03] select-none transition-opacity group-hover:opacity-[0.05] ${isActive ? 'text-white' : 'text-primary'}`}>
                  {stepNumber}
                </div>

                <div className="flex items-start justify-between gap-3 mb-4">
                  <div
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] ${
                      isActive ? 'bg-white/10 border-white/20 text-white' : stageStatus.badge
                    }`}
                  >
                    <StageStatusIcon className="h-3 w-3" />
                    {stageStatus.label}
                  </div>
                  <div className={`text-xs font-medium uppercase tracking-wider ${isActive ? 'text-white/70' : 'text-slate-500'}`}>
                    {stage.period}
                  </div>
                </div>

                <div className="relative">
                  <div className={`text-xs font-bold uppercase tracking-[0.2em] mb-1 ${isActive ? 'text-white/50' : 'text-primary/40'}`}>
                    Fase {stepNumber}
                  </div>
                  <div className={`text-xl font-bold tracking-tight leading-tight ${isActive ? 'text-white' : 'text-slate-950'}`}>
                    {stage.name}
                  </div>
                </div>

                {isActive && (
                  <motion.div 
                    layoutId="active-indicator"
                    className="absolute bottom-0 left-0 h-1 bg-secondary w-full"
                  />
                )}
              </motion.button>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.25 }}
          className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm"
        >
          <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
            <div>
              <div className="flex items-center justify-between gap-4 mb-4">
                <div>
                  <div className="text-sm text-slate-500">{activeStage.period}</div>
                  <h3 className="text-3xl text-slate-950 font-semibold tracking-tight">{activeStage.name}</h3>
                </div>
                <div
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs uppercase tracking-[0.2em] ${statusMeta.badge}`}
                >
                  <StatusIcon className="h-3.5 w-3.5" />
                  {statusMeta.label}
                </div>
              </div>

              {activeStage.description && (
                <p className="text-slate-600 leading-relaxed mb-5">{activeStage.description}</p>
              )}

              {isUpcomingStage ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-100 p-8 md:p-10">
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Contenido multimedia</div>
                  <div className="text-3xl text-slate-950 font-semibold tracking-tight mb-3">Proximamente</div>
                  <p className="text-slate-600 leading-relaxed">
                    Esta etapa estará disponible con fotos, videos y recorrido 360 cuando inicie su ejecución.
                  </p>
                </div>
              ) : (
                <>
                  {selectedMedia && (
                    <div className="mt-4">
                      <div className="mb-6 overflow-hidden rounded-2xl bg-slate-950/5 border border-slate-200">
                        {selectedMedia.type === 'video' ? (
                          <div className="aspect-video w-full">
                            <iframe
                              src={selectedMedia.src}
                              title={selectedMedia.title}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              className="h-full w-full border-0"
                            />
                          </div>
                        ) : (
                          <div className="aspect-[16/9] w-full">
                            <ImageWithFallback
                              src={selectedMedia.src}
                              alt={selectedMedia.title}
                              className="h-full w-full object-cover"
                            />
                          </div>
                        )}
                      </div>
                      <div className="text-lg text-slate-950 font-semibold">{selectedMedia.title}</div>
                      <div className="text-slate-600">{selectedMedia.description}</div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="space-y-4">
              {activeStage.deliverables && activeStage.deliverables.length > 0 && (
                <div>
                  <h4 className="text-sm uppercase tracking-[0.2em] text-slate-500 mb-3">Entregables clave</h4>
                  <div className="space-y-2">
                    {activeStage.deliverables.map((item) => (
                      <div key={item} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(isUpcomingStage || (activeStage.media && activeStage.media.length > 0)) && (
                <div>
                  <h4 className="text-sm uppercase tracking-[0.2em] text-slate-500 mb-3">Galeria interactiva</h4>
                  {isUpcomingStage ? (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-600">
                      Los archivos de esta etapa se publicarán próximamente.
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        {paginatedMedia.map((media) => {
                          const isSelected = selectedMedia?.id === media.id;
                          return (
                            <button
                              key={media.id}
                              type="button"
                              onClick={() => setSelectedMediaId(media.id)}
                              className={`group relative overflow-hidden rounded-xl border text-left transition-all ${
                                isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-slate-200 hover:border-slate-300'
                              }`}
                            >
                              <div className="aspect-[4/3]">
                                <ImageWithFallback src={media.thumbnail} alt={media.title} className="w-full h-full object-cover" />
                              </div>
                              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 to-transparent" />
                              <div className="absolute left-2 top-2 rounded-full bg-white/90 px-2 py-1 text-[11px] font-medium text-slate-800">
                                {mediaTypeBadge(media.type)}
                              </div>
                              <div className="absolute left-3 right-3 bottom-3 text-white text-sm font-medium leading-tight">
                                {media.title}
                              </div>
                              {media.type === 'video' && <PlayCircle className="absolute right-3 top-3 h-5 w-5 text-white/90" />}
                            </button>
                          );
                        })}
                      </div>

                      {pageCount > 1 && (
                        <div className="mt-4 flex items-center justify-between gap-3">
                          <button
                            type="button"
                            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                            className="rounded-full px-4 py-2 text-sm border border-slate-200 bg-white text-slate-700 disabled:opacity-45 disabled:cursor-not-allowed"
                          >
                            Anterior
                          </button>
                          <div className="text-sm text-slate-600">
                            Página {currentPage} de {pageCount}
                          </div>
                          <button
                            type="button"
                            onClick={() => setCurrentPage((prev) => Math.min(pageCount, prev + 1))}
                            disabled={currentPage === pageCount}
                            className="rounded-full px-4 py-2 text-sm border border-slate-200 bg-white text-slate-700 disabled:opacity-45 disabled:cursor-not-allowed"
                          >
                            Siguiente
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
